import React, { useEffect, useState } from "react";
import Cookies from "js-cookie";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate, useLocation } from "react-router-dom";
import moment from "moment";
import { getNotifications, markAsRead, deleteAllNotifications, updateNotificationPreferences } from "../../Redux/Slice/NotificationSlice";
import { User } from "../../Redux/Slice/Auth";
import { FiBell, FiTrash2, FiUserPlus, FiMessageSquare, FiFilter, FiCheckCircle, FiX, FiAlertTriangle, FiSettings, FiHelpCircle, FiPlusCircle, FiShield, FiClock, FiBriefcase, FiFileText, FiUsers } from "react-icons/fi";
import { FaTag, FaRegCalendarAlt, FaRegFileAlt, FaInfo, FaCloudUploadAlt } from "react-icons/fa";
import { MdOutlineSecurityUpdateGood } from "react-icons/md";
import { toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";

const ViewAllNotification = () => {
    const dispatch = useDispatch();
    const navigate = useNavigate();
    const location = useLocation();
    const { allNotifications } = useSelector((state) => state.Notification);
    const { isdata } = useSelector((state) => state.auth);
    const isMain = isdata?.data?.IsMain;
    const [filterType, setFilterType] = useState("All");
    const [search, setSearch] = useState("");
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [isSettingsModalOpen, setIsSettingsModalOpen] = useState(false);
    const [selectedPreferences, setSelectedPreferences] = useState(isdata?.data?.NotificationPreferences || []);
    const [activeInfo, setActiveInfo] = useState(null); // Track which info tooltip is open
    const [isLiveToastEnabled, setIsLiveToastEnabled] = useState(
        localStorage.getItem("liveToastNotification") !== "false"
    );

    const toggleLiveNotification = () => {
        const newValue = !isLiveToastEnabled;
        setIsLiveToastEnabled(newValue);
        localStorage.setItem("liveToastNotification", newValue);
        toast.success(`Live Toast Notifications turned ${newValue ? "ON" : "OFF"}`, {
            position: "top-right",
            autoClose: 1500,
        });
    };

    const loginData = Cookies.get("loginData");
    const parsedData = loginData ? JSON.parse(loginData) : null;
    const userId = parsedData?.data?._id;
    const userRole = parsedData?.data?.Role;

    useEffect(() => {
        if (userId) {
            dispatch(getNotifications({ userId, includeCleared: true }));
        }
    }, [userId, dispatch]);

    useEffect(() => {
        if (isdata?.data?.NotificationPreferences) {
            setSelectedPreferences(isdata.data.NotificationPreferences);
        }
    }, [isdata]);

    useEffect(() => {
        const queryParams = new URLSearchParams(location.search);
        if (queryParams.get("settings") === "true") {
            setIsSettingsModalOpen(true);
        }
    }, [location.search]);

    const handleClearAll = () => {
        setIsModalOpen(true);
    };

    const confirmClearAll = () => {
        if (userId) {
            dispatch(deleteAllNotifications(userId));
            setIsModalOpen(false);
            toast.success("All Notifications Cleared Successfully!", {
                position: "top-right",
                autoClose: 1500,
                hideProgressBar: false,
                closeOnClick: true,
            });
        }
    };

    const handleSavePreferences = async () => {
        if (userId) {
            await dispatch(updateNotificationPreferences({ userId, preferences: selectedPreferences }));
            // REFRESH user profile in Auth state so NotificationPreferences is updated
            dispatch(User(userId));
            dispatch(getNotifications({ userId, includeCleared: true }));
            dispatch(getNotifications({ userId, includeCleared: false }));

            // Also synchronize loginData cookie immediately
            const loginDataStr = Cookies.get("loginData");
            if (loginDataStr) {
                try {
                    const parsed = JSON.parse(loginDataStr);
                    if (parsed?.data) {
                        parsed.data.NotificationPreferences = selectedPreferences;
                        Cookies.set("loginData", JSON.stringify(parsed), { expires: 7 });
                    }
                } catch (e) {
                    console.error("Cookie update error", e);
                }
            }

            setIsSettingsModalOpen(false);
            toast.success("Notification preferences updated!", {
                position: "top-right",
                autoClose: 1500,
            });
        }
    };

    const togglePreference = (pref) => {
        if (pref === "All") {
            if (selectedPreferences.includes("All")) {
                setSelectedPreferences([]);
            } else {
                setSelectedPreferences(["All"]);
            }
            return;
        }

        const newPrefs = selectedPreferences.filter(p => p !== "All");
        if (newPrefs.includes(pref)) {
            setSelectedPreferences(newPrefs.filter(p => p !== pref));
        } else {
            setSelectedPreferences([...newPrefs, pref]);
        }
    };

    const groupedPrefOptions = [
        {
            group: "Task Management",
            options: [
                {
                    label: "New Task Generated",
                    value: "New Task Generated",
                    icon: <FiPlusCircle className="text-blue-500" />,
                    info: "Get notified immediately when a senior developer creates a new task or requirement in the system.",
                    example: "New Task (Call No: 1055) has been generated by Senior KALPESH DILKHUSH at 10:30 AM."
                },
                {
                    label: "Task Assigned",
                    value: "Task Assigned",
                    icon: <FiUserPlus className="text-indigo-500" />,
                    info: "Get notified when a task is assigned to a developer or support executive, helping you monitor the current assignment flow.",
                    example: "Senior KALPESH DILKHUSH has assigned the task (Call No: 1052) to Rahul at 11:30 AM."
                },
                {
                    label: "Task Accepted",
                    value: "Task Accepted",
                    icon: <FiBriefcase className="text-cyan-500" />,
                    info: "Know exactly when a developer starts working on an assigned task.",
                    example: "Task (Call No: 1052) has been accepted by Rahul at 11:45 AM."
                },
                {
                    label: "Task Solved",
                    value: "Task Solved",
                    icon: <FiMessageSquare className="text-blue-400" />,
                    info: "Get notified when a developer provides a solution and marks the task as solved, pending final completion.",
                    example: "Task (Call No: 1052) has been solved by Rahul at 04:30 PM."
                },
                {
                    label: "Task Completed",
                    value: "Task Completed",
                    icon: <FiCheckCircle className="text-green-500" />,
                    info: "Receive an alert when a developer marks their assigned task as completed, signaling it's ready for your review.",
                    example: "Task (Call No: 1052) has been completed by Rahul at 04:45 PM."
                },
                {
                    label: "Task Verified",
                    value: "Task Verified",
                    icon: <FiShield className="text-purple-500" />,
                    info: "Confirmation of tasks that have been verified and rated. Includes developer names and performance ratings.",
                    example: "Task Verified Call No : 1052. Developer: Rahul - Ratings: 5⭐, 4⭐."
                },
                {
                    label: "Developer Tasks",
                    value: "New Developer Task",
                    icon: <FaRegFileAlt className="text-orange-500" />,
                    info: "Alerts for internal R&D tasks created by senior developers for the technical team.",
                    example: "Developer Task (Call No: 882) has been generated by Senior VIPUL KANTHARIA at 10:00 AM."
                },
            ]
        },
        {
            group: "Operational Modules",
            options: [
                { label: "Employee Updates", value: "Employee", icon: <FiUsers className="text-teal-500" />, info: "Track new employee registrations and profile creations in the HR module.", example: "New Employee Profile Created: Amit Sharma (Web Developer) registered successfully." },
                { label: "Leave Requests", value: "Leave", icon: <FiClock className="text-red-500" />, info: "Real-time alerts for leave applications, including advance, half-day, and emergency leave requests.", example: "Rahul has applied for Advance Leave." },
                { label: "OCR/Scan Updates", value: "OCR/Scan", icon: <FaCloudUploadAlt className="text-sky-500" />, info: "Updates regarding OCR plan activations, API count increases, and scanning status.", example: "A new OCR plan or API count has been added." },
                { label: "Client Activities", value: "Client", icon: <FiBriefcase className="text-amber-500" />, info: "Notifications for new client account registrations and company profile additions.", example: "New Client Account Registered: 'Global Logistics Pvt Ltd' added to the database." },
                { label: "GSTR Compliance", value: "GstR & Compliance", icon: <FiFileText className="text-emerald-500" />, info: "Alerts for new GSTIN setups, updates, or deletions for client compliance tracking.", example: "A new GSTIN has been added for client \"TechCorp\"." }
            ]
        }
    ];

    const handleNotificationClick = (notif) => {
        if (userId) {
            dispatch(markAsRead({ notificationId: notif._id, userId }));
        }

        if (notif.type === "Task" && notif.relatedId) {
            const seniorRoles = ["owner", "Web Senior Developer", "Delphi Senior Developer", "management"];
            const prefix = seniorRoles.includes(userRole) ? "/InhouseRnd" : "/CustomerReq";
            navigate(`${prefix}/CustomerrequustDetail/${notif.relatedId}`);
        }

        if (notif.type === "Leave") {
            if (userRole === "owner") {
                navigate("/OwnerLeaveInbox");
            } else if (["Web Junior Developer", "Delphi Junior Developer", "Data Analyst", "Junior Customer Support", "inhouse"].includes(userRole)) {
                navigate("/MyRequest");
            } else {
                if (isMain) {
                    navigate("/GeneralManagerRequest");
                } else {
                    navigate("/ProjectManagerRequest");
                }
            }
        }
    };

    const getIconInfo = (type) => {
        switch (type) {
            case "Task":
                return { icon: <FaRegFileAlt className="text-orange-500 text-xl" />, iconBg: "bg-orange-50" };
            case "Employee":
                return { icon: <FiUserPlus className="text-green-500 text-xl" />, iconBg: "bg-green-50" };
            case "Leave":
                return { icon: <FaRegCalendarAlt className="text-pink-500 text-xl" />, iconBg: "bg-pink-50" };
            case "WhatsApp":
                return { icon: <FiMessageSquare className="text-emerald-500 text-xl" />, iconBg: "bg-emerald-50" };
            case "OCR/Scan":
                return { icon: <FaRegFileAlt className="text-blue-500 text-xl" />, iconBg: "bg-blue-50" };
            case "Client":
                return { icon: <FaTag className="text-purple-500 text-xl" />, iconBg: "bg-purple-50" };
            case "Update":
                return { icon: <FaCloudUploadAlt className="text-indigo-500 text-xl" />, iconBg: "bg-indigo-50" };
            case "GSTR Compliance":
                return { icon: <MdOutlineSecurityUpdateGood className="text-cyan-500 text-xl" />, iconBg: "bg-cyan-50" };
            default:
                return { icon: <FaInfo className="text-gray-500 text-xl" />, iconBg: "bg-gray-50" };
        }
    };

    const categories = ["All", "Unread", "Read", "Task", "Employee", "Leave", "OCR/Scan", "Client", "GstR & Compliance"];
    const juniorRoles = ["Web Junior Developer", "Delphi Junior Developer", "Data Analyst", "Junior Customer Support", "inhouse"];
    const isJunior = juniorRoles.includes(userRole);

    const ownerBaseCategories = ["All", "Unread", "Read"];
    const ownerSelectedCats = selectedPreferences.includes("All")
        ? ["New Task Generated", "Task Assigned", "Task Completed", "Task Verified", "Employee", "Leave", "OCR/Scan", "Client", "GstR & Compliance"]
        : selectedPreferences.filter(p => !["Unread", "Read"].includes(p));

    const currentCategories = userRole === "owner"
        ? [...new Set([...ownerBaseCategories, ...ownerSelectedCats])]
        : (isJunior ? ["All", "Unread", "Read", "Task", "Leave"] : categories);

    const restrictedTitles = ["New Daily Report", "Leave Request Submitted"];

    const accessibleNotifications = allNotifications?.filter(notif => {
        const isRestricted = restrictedTitles.some(title => notif.title?.includes(title));
        return !(isRestricted && !isJunior);
    }) || [];

    const filteredNotifications = accessibleNotifications.filter(notif => {
        // Filter by Status (Unread / Read)
        if (filterType === "Unread" && !notif.isUnread) return false;
        if (filterType === "Read" && notif.isUnread) return false;

        // Filter by Type (Task, Employee, etc. - only if not "All", "Unread", or "Read")
        const statusFilters = ["All", "Unread", "Read"];

        let matchesType = false;
        if (statusFilters.includes(filterType)) {
            matchesType = true;
        } else if (userRole === "owner") {
            matchesType = notif.type === filterType || notif.title === filterType;
            // Legacy mapping for "Customer Support"
            if (!matchesType && filterType === "Customer Support") {
                matchesType = notif.title === "New Customer Requirement";
            }
        } else if (filterType === "Customer Support") {
            matchesType = notif.title === "New Customer Requirement";
        } else if (filterType === "Task Assigned" || filterType === "Task Verified") {
            matchesType = notif.title === filterType;
        } else {
            matchesType = notif.type === filterType;
        }

        const matchesSearch =
            notif.title.toLowerCase().includes(search.toLowerCase()) ||
            notif.message.toLowerCase().includes(search.toLowerCase());

        return matchesType && matchesSearch;
    });

    return (
        <>
            <div className="p-4 sm:px-4 relative">
                <div className="max-w-8xl mx-auto">
                    {/* Responsive Header Section */}
                    <div className="flex flex-col lg:flex-row justify-between items-stretch lg:items-center gap-6 mb-10 bg-white p-5 sm:p-6 rounded-[32px] shadow-[0_4px_25px_rgba(0,0,0,0.02)] border border-gray-100">
                        <div className="flex items-center gap-4">
                            <div className="p-2 sm:p-3 bg-blue-50/80 rounded-xl flex items-center justify-center shrink-0">
                                <FiBell className="text-blue-600 text-xl sm:text-2xl animate-pulse" />
                            </div>
                            <div>
                                <h1 className="text-xl sm:text-2xl font-extrabold text-gray-800 tracking-tight leading-tight">Notification Center</h1>
                                <p className="text-gray-400 text-[12px] font-semibold ml-0.5 mt-0.5 tracking-wide uppercase">
                                    {accessibleNotifications.filter(n => n.isUnread).length || 0} New Updates Pending
                                </p>
                            </div>
                        </div>

                        <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-3">
                            <div className="relative flex-1 group">
                                <input
                                    type="text"
                                    value={search}
                                    onChange={(e) => setSearch(e.target.value)}
                                    placeholder="Search notifications..."
                                    className="w-full pl-11 pr-4 py-3 sm:py-2.5 bg-gray-50/50 border border-gray-100 rounded-xl focus:outline-none focus:border-[#133A69] transition-all text-sm font-semibold text-gray-700 placeholder:text-gray-300"
                                />
                                <FiFilter className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-300 group-focus-within:text-[#133A69] transition-colors" size={18} />
                            </div>

                            <div className="flex gap-2">
                                <button
                                    onClick={toggleLiveNotification}
                                    className={`flex items-center justify-center gap-2 px-3 py-2 sm:py-2 rounded-xl font-black text-[13px] transition-all border active:scale-95 shrink-0 shadow-sm ${
                                        isLiveToastEnabled 
                                            ? "bg-emerald-50 text-emerald-600 border-emerald-200 hover:bg-emerald-100" 
                                            : "bg-gray-50 text-gray-500 border-gray-200 hover:bg-gray-100"
                                    }`}
                                    title="Toggle Live Popup Notifications"
                                >
                                    <FiBell size={15} className={isLiveToastEnabled ? "animate-pulse" : "opacity-50"} />
                                    <span className="hidden sm:inline">Live Toast: </span>{isLiveToastEnabled ? "ON" : "OFF"}
                                </button>

                                {userRole === "owner" && (
                                    <button
                                        onClick={() => setIsSettingsModalOpen(true)}
                                        className="flex items-center justify-center p-2.5 bg-blue-50 text-blue-600 rounded-xl hover:bg-blue-100 transition-all border border-blue-100 active:scale-95"
                                        title="Notification Settings"
                                    >
                                        <FiSettings size={20} />
                                    </button>
                                )}
                                <button
                                    onClick={handleClearAll}
                                    className="flex items-center justify-center gap-2 px-3 py-2 sm:py-2 bg-red-50 text-red-600 rounded-xl font-black text-sm hover:bg-red-100 transition-all border border-red-100 active:scale-95 shrink-0"
                                >
                                    <FiTrash2 size={16} />
                                    Clear All
                                </button>
                            </div>
                        </div>
                    </div>

                    <div className="contain-size mb-16">
                        <div className="flex gap-2 mb-2 overflow-x-auto pb-2 scroll-smooth whitespace-nowrap 
    [&::-webkit-scrollbar]:hidden [-ms-overflow-style:none] [scrollbar-width:none]">
                            {currentCategories.map((cat) => (
                                <button
                                    key={cat}
                                    onClick={() => setFilterType(cat)}
                                    className={`px-4 py-2 ms-[2px] cursor-pointer rounded-xl text-sm font-bold whitespace-nowrap flex-shrink-0 transition-all duration-300 border ${filterType === cat
                                        ? "bg-[#133A69] text-white border-[#133A69] shadow-lg shadow-blue-900/10 scale-105"
                                        : "bg-white text-gray-400 border-transparent hover:border-gray-200 hover:text-gray-600 shadow-sm"
                                        }`}
                                >
                                    {cat}
                                </button>
                            ))}
                        </div>
                    </div>

                    <div className="grid grid-cols-1 gap-3 sm:gap-4 pr-2 max-h-[calc(100vh-350px)] overflow-y-auto">
                        {filteredNotifications?.length > 0 ? (
                            filteredNotifications.map((notif) => {
                                const { icon, iconBg } = getIconInfo(notif.type);
                                const isUnread = notif.isUnread;
                                return (
                                    <div
                                        key={notif._id}
                                        onClick={() => handleNotificationClick(notif)}
                                        className={`group relative flex flex-col sm:flex-row items-start sm:items-center gap-4 p-4 sm:p-4 bg-white rounded-[24px] border border-gray-100 shadow-[0_4px_15px_rgba(0,0,0,0.02)] hover:shadow-[0_12px_35px_rgba(0,0,0,0.08)] hover:border-blue-100 transition-all duration-300 cursor-pointer overflow-hidden ${isUnread ? 'before:absolute before:left-0 before:top-0 before:bottom-0 before:w-1.5 before:bg-blue-500' : ''}`}
                                    >
                                        {/* Icon Column */}
                                        <div className="flex items-center justify-between w-full sm:w-auto">
                                            <div className={`w-12 h-12 shrink-0 rounded-xl flex items-center justify-center transition-transform group-hover:-rotate-6 duration-300 ${iconBg} shadow-inner`}>
                                                {icon}
                                            </div>
                                            {/* Date Badge (Mobile Only - Top Right of Card Header) */}
                                            <div className="sm:hidden flex items-center gap-2">
                                                {isUnread && (
                                                    <div className="w-2 h-2 bg-blue-500 rounded-full animate-pulse"></div>
                                                )}
                                                <span className="text-[10px] font-bold text-gray-400 bg-gray-50 px-2 py-1 rounded-lg border border-gray-100 uppercase tracking-wider">
                                                    {moment(notif?.createdAt).format('MMM D')}
                                                </span>
                                            </div>
                                        </div>

                                        {/* Content Column */}
                                        <div className="flex-1 min-w-0 pr-0 sm:pr-10">
                                            <div className="flex justify-between items-start">
                                                <h3 className={`text-[15px] sm:text-[17px] leading-tight line-clamp-1 pr-2 tracking-tight ${isUnread ? 'font-extrabold text-gray-900' : 'font-semibold text-gray-600'}`}>
                                                    {notif.title}
                                                </h3>
                                                {/* Date Badge (Desktop Only) */}
                                                <span className="hidden sm:block text-[11px] font-bold text-gray-400 bg-gray-50 px-3 py-1 rounded-full border border-gray-100 uppercase tracking-widest shrink-0 whitespace-nowrap">
                                                    {moment(notif?.createdAt).format('MMM D, YYYY')}
                                                </span>
                                            </div>

                                            <p className={`text-[13px] sm:text-[14px] leading-relaxed line-clamp-2 sm:line-clamp-1 mb-2 mt-1 ${isUnread ? 'text-gray-600 font-medium' : 'text-gray-400 font-normal'}`}>
                                                {notif.message}
                                            </p>

                                            <div className="flex items-center gap-3">
                                                <span className="text-[10px] font-bold text-blue-500/60 bg-blue-50/50 px-2 py-0.5 rounded-md uppercase tracking-widest">{notif.type}</span>
                                                <span className="text-[11px] font-medium text-gray-300">•</span>
                                                <span className="text-[10px] sm:text-[11px] font-bold text-gray-400 capitalize whitespace-nowrap">{moment(notif?.createdAt).fromNow()}</span>
                                            </div>
                                        </div>

                                        {/* Actions (Desktop Only) */}
                                        <div className="absolute right-6 top-1/2 -translate-y-1/2 opacity-0 group-hover:opacity-100 transition-all duration-300 hidden md:block">
                                            <div className="p-2.5 bg-blue-50 text-blue-600 rounded-full shadow-sm hover:scale-110 active:scale-95 transition-transform">
                                                <FiCheckCircle size={20} />
                                            </div>
                                        </div>

                                        {/* Unread Status (Desktop Only Right Badge) */}
                                        {isUnread && (
                                            <div className="hidden sm:block absolute right-3 top-3 w-1.5 h-1.5 bg-blue-500 rounded-full shadow-[0_0_8px_rgba(59,130,246,0.5)]"></div>
                                        )}
                                    </div>
                                );
                            })
                        ) : (
                            <div className="py-20 flex flex-col items-center justify-center bg-white rounded-[40px] border border-dashed border-gray-200 shadow-inner">
                                <div className="w-24 h-24 bg-gray-50 rounded-full flex items-center justify-center mb-6">
                                    <FiBell className="text-gray-300 text-4xl" />
                                </div>
                                <h3 className="text-gray-800 font-black text-xl mb-1 uppercase tracking-tighter italic text-center">Relaxing Time?</h3>
                                <p className="text-gray-400 font-medium text-center px-4">No {filterType !== "All" ? `${filterType} ` : ""}notifications found here.</p>
                            </div>
                        )}
                    </div>
                </div>

                {/* Custom Modal for Clear All */}
                {isModalOpen && (
                    <div className="fixed inset-0 z-[100] flex items-center justify-center p-4 sm:p-6 overflow-hidden">
                        {/* Backdrop */}
                        <div
                            className="absolute inset-0 bg-gray-900/60 backdrop-blur-sm animate-in fade-in duration-300"
                            onClick={() => setIsModalOpen(false)}
                        ></div>

                        {/* Modal Content */}
                        <div className="relative bg-white w-full max-w-[420px] rounded-[32px] p-8 shadow-[0_20px_60px_rgba(0,0,0,0.2)] border border-gray-100 animate-in zoom-in-95 slide-in-from-bottom-5 duration-300">
                            <button
                                onClick={() => setIsModalOpen(false)}
                                className="absolute right-6 top-6 p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-50 rounded-full transition-all"
                            >
                                <FiX size={20} />
                            </button>

                            <div className="flex flex-col items-center text-center">
                                <div className="w-16 h-16 bg-red-50 rounded-full flex items-center justify-center mb-6">
                                    <FiAlertTriangle className="text-red-500 text-3xl animate-bounce" />
                                </div>

                                <h3 className="text-2xl font-black text-gray-800 mb-2">Clear History?</h3>
                                <p className="text-gray-500 font-medium text-[15px] max-w-[280px]">
                                    This will permanently remove all notifications from your center. This cannot be undone.
                                </p>

                                <div className="flex flex-col sm:flex-row gap-3 w-full mt-8">
                                    <button
                                        onClick={() => setIsModalOpen(false)}
                                        className="flex-1 px-6 py-3 bg-gray-50 text-gray-600 rounded-2xl font-bold text-sm hover:bg-gray-100 transition-all border border-gray-100"
                                    >
                                        No, Keep them
                                    </button>
                                    <button
                                        onClick={confirmClearAll}
                                        className="flex-1 px-6 py-3 bg-[#133A69] text-white rounded-2xl font-bold text-sm hover:bg-[#0e2a4d] transition-all shadow-lg shadow-blue-900/20"
                                    >
                                        Yes, Clear All
                                    </button>
                                </div>
                            </div>
                        </div>
                    </div>
                )}
                {/* Settings Modal */}
                {isSettingsModalOpen && (
                    <div className="fixed inset-0 z-[100] flex items-center justify-center p-4 sm:p-6 overflow-hidden">
                        <div
                            className="absolute inset-0 bg-gray-900/60 backdrop-blur-sm animate-in fade-in duration-300"
                            onClick={() => setIsSettingsModalOpen(false)}
                        ></div>

                        <div className="relative bg-white w-full max-w-[680px] rounded-[32px] p-0 shadow-[0_25px_80px_rgba(0,0,0,0.2)] border border-gray-100 animate-in zoom-in-95 slide-in-from-bottom-5 duration-300 flex flex-col max-h-[85vh]">
                            {/* Modal Header */}
                            <div className="p-6 border-b border-gray-50 flex justify-between items-center bg-gray-50/40 rounded-t-[32px]">
                                <div className="flex items-center gap-3">
                                    <div className="w-9 h-9 bg-[#133A69] rounded-lg flex items-center justify-center shadow-md shadow-blue-900/10">
                                        <FiBell className="text-white text-lg" />
                                    </div>
                                    <div>
                                        <h3 className="text-xl font-black text-gray-900 tracking-tight">Notification Settings</h3>
                                        <p className="text-[12px] text-gray-500 font-medium">Customize your administrative notification preferences.</p>
                                    </div>
                                </div>
                                <button
                                    onClick={() => setIsSettingsModalOpen(false)}
                                    className="p-1.5 text-gray-400 hover:text-gray-600 hover:bg-white shadow-sm rounded-full transition-all border border-transparent hover:border-gray-100"
                                >
                                    <FiX size={20} />
                                </button>
                            </div>

                            {/* Modal Body */}
                            <div className="flex-1 overflow-y-auto p-5 custom-scrollbar bg-[#fcfdfe]">
                                <div className="space-y-6">
                                    {/* Master Global Toggle */}
                                    <label
                                        className={`flex items-center justify-between p-4 rounded-[24px] border-2 transition-all cursor-pointer group ${selectedPreferences.includes("All")
                                            ? "bg-[#133A69] border-[#133A69] shadow-lg shadow-blue-900/20"
                                            : "bg-white border-gray-100 hover:border-blue-200"
                                            }`}
                                    >
                                        <div className="flex items-center gap-4">
                                            <div className={`w-11 h-11 rounded-xl flex items-center justify-center transition-colors ${selectedPreferences.includes("All") ? "bg-white/10" : "bg-blue-50"}`}>
                                                <FiBell className={`text-xl ${selectedPreferences.includes("All") ? "text-white" : "text-[#133A69]"}`} />
                                            </div>
                                            <div>
                                                <span className={`text-[17px] font-black uppercase tracking-tight block ${selectedPreferences.includes("All") ? "text-white" : "text-gray-900"}`}>
                                                    All System Alerts
                                                </span>
                                                <span className={`text-[13px] font-medium ${selectedPreferences.includes("All") ? "text-blue-100/70" : "text-gray-500"}`}>
                                                    Full operational oversight across all modules.
                                                </span>
                                            </div>
                                        </div>
                                        <div className={`w-11 h-6 rounded-full p-1 transition-all duration-300 ${selectedPreferences.includes("All") ? "bg-blue-400" : "bg-gray-200"}`}>
                                            <div className={`w-4 h-4 bg-white rounded-full shadow-sm transition-all duration-300 transform ${selectedPreferences.includes("All") ? "translate-x-5" : "translate-x-0"}`}></div>
                                        </div>
                                        <input
                                            type="checkbox"
                                            className="hidden"
                                            checked={selectedPreferences.includes("All")}
                                            onChange={() => togglePreference("All")}
                                        />
                                    </label>

                                    {/* Category Groups */}
                                    <div className="space-y-8">
                                        {groupedPrefOptions.map((group) => (
                                            <div key={group.group}>
                                                <div className="flex items-center gap-3 mb-4">
                                                    <h4 className="text-[10px] font-black text-[#133A69] uppercase tracking-[0.2em] bg-blue-50/50 px-3 py-1 rounded-lg">
                                                        {group.group}
                                                    </h4>
                                                    <div className="h-[1px] flex-1 bg-gradient-to-r from-gray-200 to-transparent"></div>
                                                </div>

                                                <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                                                    {group.options.map((opt) => (
                                                        <div key={opt.value} className="relative h-full">
                                                            <label
                                                                className={`flex flex-col h-full p-3 rounded-[22px] border-2 transition-all cursor-pointer ${selectedPreferences.includes(opt.value) || selectedPreferences.includes("All")
                                                                    ? "bg-blue-50/30 border-blue-100 shadow-sm ring-2 ring-blue-50/30"
                                                                    : "bg-white border-gray-50 hover:border-gray-200"
                                                                    } ${selectedPreferences.includes("All") ? "opacity-75 cursor-not-allowed" : "active:scale-[0.98]"}`}
                                                            >
                                                                <div className="flex items-center justify-between mb-2">
                                                                    <div className="flex items-center gap-2">
                                                                        <div className="text-lg">
                                                                            {opt.icon}
                                                                        </div>
                                                                        <span className={`text-[16px] font-bold tracking-tight ${selectedPreferences.includes(opt.value) || selectedPreferences.includes("All") ? "text-[#133A69]" : "text-gray-800"
                                                                            }`}>
                                                                            {opt.label}
                                                                        </span>
                                                                    </div>
                                                                    <input
                                                                        type="checkbox"
                                                                        disabled={selectedPreferences.includes("All")}
                                                                        checked={selectedPreferences.includes(opt.value) || selectedPreferences.includes("All")}
                                                                        onChange={() => togglePreference(opt.value)}
                                                                        className="w-4 h-4 rounded border-2 border-gray-300 text-[#133A69] focus:ring-blue-500/20 cursor-pointer accent-[#133A69]"
                                                                    />
                                                                </div>

                                                                <p className={`text-[13px] font-medium leading-relaxed mb-3 flex-1 ${selectedPreferences.includes(opt.value) || selectedPreferences.includes("All") ? "text-gray-600" : "text-gray-500"
                                                                    }`}>
                                                                    {opt.info}
                                                                </p>

                                                                <div className={`mt-auto p-2.5 rounded-xl border-l-4 ${selectedPreferences.includes(opt.value) || selectedPreferences.includes("All")
                                                                    ? "bg-white border-[#133A69]"
                                                                    : "bg-gray-50 border-gray-200"
                                                                    }`}>
                                                                    <span className="block text-[10px] font-black uppercase tracking-wider text-gray-400 mb-0.5">Preview</span>
                                                                    <p className="text-[12px] font-bold text-gray-700 leading-tight">
                                                                        {opt.example}
                                                                    </p>
                                                                </div>
                                                            </label>
                                                        </div>
                                                    ))}
                                                </div>
                                            </div>
                                        ))}
                                    </div>
                                </div>
                            </div>

                            {/* Modal Footer */}
                            <div className="p-6 border-t border-gray-50 bg-white rounded-b-[32px] flex gap-3">
                                <button
                                    onClick={() => setIsSettingsModalOpen(false)}
                                    className="flex-1 px-6 py-3 bg-white text-gray-500 rounded-xl font-bold text-xs hover:bg-gray-50 transition-all border border-gray-200 active:scale-95"
                                >
                                    Discard
                                </button>
                                <button
                                    onClick={handleSavePreferences}
                                    className="flex-[1.5] px-6 py-3 bg-[#133A69] text-white rounded-xl font-bold text-xs hover:bg-[#0e2a4d] transition-all shadow-lg active:scale-95"
                                >
                                    Apply Settings
                                </button>
                            </div>
                        </div>
                    </div>
                )}
            </div>
        </>
    );
};

export default ViewAllNotification;